<template>
  <div class="relative w-full">
    <UpdatedTopAppBar
      v-if="topAppBarItemsStore.updatedVersion"
      :user-name="authStore.username"
      :menu-items="showHamburger ? [] : topAppBarItemsStore.menuItems"
    >
      <UpdatedTopAppBarMenuItem
        v-if="topAppBarItemsStore.settingsMenuItem !== undefined && !showHamburger"
        :id="topAppBarItemsStore.settingsMenuItem.id"
        :icon-name="topAppBarItemsStore.settingsMenuItem?.iconName"
        :external="topAppBarItemsStore.settingsMenuItem?.external"
        :route="topAppBarItemsStore.settingsMenuItem?.route"
      />
      <template #profile-dropdown>
        <TopAppBarMenuItemDropdownLink
          id="top_appbar_dropdown_username_logout"
          text="Logout"
          @click.prevent="onLogout()"
        />
        <TopAppBarMenuItemDropdownLink
          v-if="showSettingsMenuItem"
          id="top_appbar_dropdown_username_settings"
          text="Settings"
          :route="topAppBarItemsStore.userSettingsRoute"
          :external="true"
          target="_self"
        />
        <TopAppBarMenuItemDropdownDivider />
        <TopAppBarMenuItemDropdownLink
          id="top_appbar_dropdown_username_get_training"
          text="Get training"
          route="https://skynamo.com/training/"
          :external="true"
        />
        <TopAppBarMenuItemDropdownLink
          id="top_appbar_dropdown_username_help"
          text="Support desk"
          route="https://support.skynamo.com/support/solutions"
          :external="true"
        />
        <TopAppBarMenuItemDropdownLink
          id="top_appbar_dropdown_username_feedback"
          text="Add an idea"
          route="https://portal.skynamo.com/"
          :external="true"
        />
        <TopAppBarMenuItemDropdownDivider />
        <TopAppBarMenuItemDropdownText
          id="top_appbar_dropdown_username_organisation"
          heading="Organisation"
          :text="authStore.instanceName"
        />
        <TopAppBarMenuItemDropdownDivider />
        <TopAppBarMenuItemDropdownText
          v-if="showVersionMenuItem"
          id="top_appbar_dropdown_username_skynamo_version"
          text="Skynamo 2.0"
        />
      </template>
    </UpdatedTopAppBar>

    <TopAppBar
      v-else
      :name="authStore.instanceName ? authStore.instanceName : authStore.username"
      :menu-items="showHamburger ? [] : topAppBarItemsStore.menuItems"
    >
      <TopAppBarMenuItem
        v-if="topAppBarItemsStore.settingsMenuItem !== undefined && !showHamburger"
        :id="topAppBarItemsStore.settingsMenuItem.id"
        :icon-name="topAppBarItemsStore.settingsMenuItem?.iconName"
        icon-size="18"
        :external="topAppBarItemsStore.settingsMenuItem?.external"
        :route="topAppBarItemsStore.settingsMenuItem?.route"
      />
      <template #profile-dropdown>
        <div v-if="showSettingsMenuItem">
          <TopAppBarMenuItemDropdownLink
            id="top_appbar_dropdown_username_settings"
            text="Settings"
            :route="topAppBarItemsStore.userSettingsRoute"
            :external="true"
            target="_self"
          />
          <TopAppBarMenuItemDropdownDivider />
        </div>
        <TopAppBarMenuItemDropdownLink
          id="top_appbar_dropdown_username_get_training"
          text="Get training"
          route="https://skynamo.com/training/"
          left-icon="bolt"
          :external="true"
        />
        <TopAppBarMenuItemDropdownLink
          id="top_appbar_dropdown_username_help"
          text="Support desk"
          route="https://support.skynamo.com/support/solutions"
          left-icon="contact_support"
          :external="true"
        />
        <TopAppBarMenuItemDropdownLink
          id="top_appbar_dropdown_username_feedback"
          text="Add an idea"
          route="https://portal.skynamo.com/"
          left-icon="lightbulb"
          :external="true"
        />
        <TopAppBarMenuItemDropdownDivider v-if="showSwitchInstancesMenuItem" />
        <TopAppBarMenuItemDropdownLink
          v-if="showSwitchInstancesMenuItem"
          id="top_appbar_dropdown_username_switch_instances"
          text="Switch instance"
          route="SwitchInstances"
          left-icon="restart_alt"
          :external="false"
        />
        <TopAppBarMenuItemDropdownDivider />
        <TopAppBarMenuItemDropdownText
          id="top_appbar_dropdown_username_email"
          class="pl-[15px] pr-6"
          :text="authStore.email"
        />
        <TopAppBarMenuItemDropdownText
          id="top_appbar_dropdown_username_logout"
          class="cursor-pointer hover:bg-neutral-10 pl-[19px]"
          text="Log out"
          left-icon="logout"
          @click.prevent="onLogout()"
        />
        <div v-if="showVersionMenuItem">
          <TopAppBarMenuItemDropdownDivider />
          <TopAppBarMenuItemDropdownText
            id="top_appbar_dropdown_username_skynamo_version"
            text="Skynamo 2.0"
          />
        </div>
      </template>
    </TopAppBar>

    <!-- Hamburger overlay — rendered outside the design-system component so it works on both
       UpdatedTopAppBar (slot always visible) AND legacy TopAppBar (right side hidden below lg). -->
    <button
      v-if="showHamburger"
      ref="hamburgerRef"
      class="absolute top-0 z-[60] h-full flex items-center justify-center cursor-pointer hover:bg-shade-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-shade-white/50"
      :style="{ right: hamburgerRightOffset, padding: '0 14px' }"
      aria-label="Open navigation"
      :aria-expanded="isMobileMenuOpen"
      @click="toggleMobileMenu"
    >
      <span
        class="material-icons"
        style="color: #fff; vertical-align: middle;"
      >{{ isMobileMenuOpen ? 'close' : 'menu' }}</span>
    </button>
  </div>

  <!-- Mobile nav dropdown — rendered outside the app bar to avoid stacking-context clipping -->
  <Teleport to="body">
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 z-40"
      @click="closeMobileMenu"
    />
    <nav
      v-if="isMobileMenuOpen"
      ref="mobileMenuRef"
      class="fixed z-50 min-w-[240px] max-w-[320px] bg-shade-white border border-neutral-40 rounded-lg shadow-lg py-1 max-h-[80vh] overflow-y-auto"
      :style="dropdownStyle"
      aria-label="Navigation menu"
      @keydown.esc="closeMobileMenu"
    >
      <!-- Main nav items -->
      <template
        v-for="item in topAppBarItemsStore.menuItems"
        :key="item.id"
      >
        <a
          v-if="item.external"
          :id="`mobile_nav_${item.id}`"
          :href="item.route"
          class="block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="closeMobileMenu"
        >{{ item.displayName }}</a>
        <button
          v-else
          :id="`mobile_nav_${item.id}`"
          class="flex w-full items-center px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="navigateInternal(item.route)"
        >
          {{ item.displayName }}
        </button>
      </template>

      <!-- Settings icon item (links to device/warehouse management on the instance) -->
      <template v-if="topAppBarItemsStore.settingsMenuItem !== undefined">
        <div
          class="my-1 border-t border-neutral-30"
          role="separator"
        />
        <a
          :id="`mobile_nav_${topAppBarItemsStore.settingsMenuItem.id}`"
          :href="topAppBarItemsStore.settingsMenuItem.route"
          class="flex items-center gap-3 px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="closeMobileMenu"
        >
          <span class="material-icons text-[18px] text-neutral-90">{{ topAppBarItemsStore.settingsMenuItem.iconName }}</span>
          <span>Settings</span>
        </a>
      </template>

      <!-- Profile items — included only when the design-system component hides the
           profile dropdown (legacy TopAppBar below lg). On UpdatedTopAppBar the
           profile dropdown stays visible to the right of the hamburger, so we
           don't duplicate here. -->
      <template v-if="includeProfileItemsInBurger">
        <div
          class="my-1 border-t border-neutral-30"
          role="separator"
        />
        <a
          v-if="showSettingsMenuItem"
          id="mobile_nav_user_settings"
          :href="topAppBarItemsStore.userSettingsRoute"
          class="block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="closeMobileMenu"
        >User settings</a>
        <a
          id="mobile_nav_get_training"
          href="https://skynamo.com/training/"
          target="_blank"
          rel="noopener"
          class="block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="closeMobileMenu"
        >Get training</a>
        <a
          id="mobile_nav_support_desk"
          href="https://support.skynamo.com/support/solutions"
          target="_blank"
          rel="noopener"
          class="block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="closeMobileMenu"
        >Support desk</a>
        <a
          id="mobile_nav_add_idea"
          href="https://portal.skynamo.com/"
          target="_blank"
          rel="noopener"
          class="block px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="closeMobileMenu"
        >Add an idea</a>
        <button
          v-if="showSwitchInstancesMenuItem"
          id="mobile_nav_switch_instances"
          class="flex w-full items-center px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="navigateInternal('SwitchInstances')"
        >
          Switch instance
        </button>

        <div
          class="my-1 border-t border-neutral-30"
          role="separator"
        />
        <div class="px-4 py-2 text-xs text-neutral-80 break-all">
          {{ authStore.email }}
        </div>
        <button
          id="mobile_nav_logout"
          class="flex w-full items-center px-4 py-3 text-sm text-neutral-100 hover:bg-neutral-10 focus:bg-neutral-10 focus:outline-none"
          @click="onLogoutFromBurger"
        >
          Log out
        </button>
      </template>
    </nav>
  </Teleport>
</template>

<script lang="ts" setup async>
import { computed, defineAsyncComponent, nextTick, ref } from 'vue'
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core'

// stores
import { useAuthStore } from '@shared/stores/auth'
import useTopAppBarItemsStore from '@shared/stores/top-app-bar'
import { useRoute, useRouter } from 'vue-router'
import { useUserInfoStore } from '@shared/stores/user-info'
import { BaseRouteNames as Routes } from '@/router/routes'

// design system
const TopAppBar = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBar.vue'))
const UpdatedTopAppBar = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/UpdatedTopAppBar.vue'))
const UpdatedTopAppBarMenuItem = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/UpdatedTopAppBarMenuItem.vue'))
const TopAppBarMenuItem = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBarMenuItem.vue'))
const TopAppBarMenuItemDropdownDivider = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBarMenuItemDropdownDivider.vue'))
const TopAppBarMenuItemDropdownLink = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBarMenuItemDropdownLink.vue'))
const TopAppBarMenuItemDropdownText = defineAsyncComponent(() => import('@shared/design-system/components/TopAppBar/TopAppBarMenuItemDropdownText.vue'))

const authStore = useAuthStore()
const topAppBarItemsStore = useTopAppBarItemsStore()
topAppBarItemsStore.update()
const userInfoStore = useUserInfoStore()
userInfoStore.update()
const router = useRouter()
const route = useRoute()

const breakpoints = useBreakpoints(breakpointsTailwind)
// UpdatedTopAppBar hides nav-tabs below xl (1280px); legacy TopAppBar hides
// the entire right-side area (slot + profile dropdown) below lg (1024px).
// Match the active version so the hamburger appears exactly when the tabs disappear.
const isXl = breakpoints.greaterOrEqual('xl')
const isLg = breakpoints.greaterOrEqual('lg')
const showHamburger = computed(() => topAppBarItemsStore.updatedVersion ? !isXl.value : !isLg.value)

// Where to place the hamburger horizontally. On UpdatedTopAppBar the profile
// dropdown is visible to its right at all widths, so we leave room for it.
// On legacy TopAppBar the profile dropdown is hidden when the hamburger shows,
// so we put it at the far right.
const hamburgerRightOffset = computed(() =>
  topAppBarItemsStore.updatedVersion ? '13rem' : '1rem'
)

const isMobileMenuOpen = ref(false)
const hamburgerRef = ref<HTMLButtonElement | null>(null)
const mobileMenuRef = ref<HTMLElement | null>(null)

const dropdownStyle = computed(() => {
  if (!hamburgerRef.value) return { top: '64px', right: '0px' }
  const rect = hamburgerRef.value.getBoundingClientRect()
  return {
    top: `${Math.round(rect.bottom) + 4}px`,
    right: `${Math.round(window.innerWidth - rect.right)}px`,
  }
})

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  if (isMobileMenuOpen.value) {
    nextTick(() => {
      mobileMenuRef.value?.querySelector<HTMLElement>('a, button')?.focus()
    })
  } else {
    hamburgerRef.value?.focus()
  }
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false
  hamburgerRef.value?.focus()
}

function navigateInternal(routeName: string | undefined) {
  if (!routeName) {
    return
  }
  router.push({ name: routeName })
  closeMobileMenu()
}

const switchInstances: Routes = 'SwitchInstances'
//@ts-expect-error because these are dynamic route names
const subscriptionManagementName: Routes = 'SubscriptionManagement'
const homeName: Routes = 'Home'

const showSettingsMenuItem = computed(() => !route.matched.some(({ name }) => name === subscriptionManagementName || name === homeName) && topAppBarItemsStore.userSettingsRoute !== undefined)
const showVersionMenuItem = computed(() => false)
const showSwitchInstancesMenuItem = computed(() => !route.matched.some(({ name }) => name === switchInstances) && !isUserSingleClientOrInstance.value)

const isUserSingleClientOrInstance = computed(() => userInfoStore.clients.length === 1 && userInfoStore.instances.length <= 1)

// The legacy TopAppBar wraps its profile dropdown in a container that's hidden below `lg`,
// so when the burger is shown on the legacy version the user otherwise loses access to
// Logout / Switch instance / support links. Mirror those into the burger in that case.
// On UpdatedTopAppBar the profile dropdown stays visible regardless of viewport, so we
// don't duplicate.
const includeProfileItemsInBurger = computed(() => !topAppBarItemsStore.updatedVersion && showHamburger.value)

const emit = defineEmits<{
  onLogout: []
}>()

function onLogout() {
  emit('onLogout')
}

function onLogoutFromBurger() {
  closeMobileMenu()
  emit('onLogout')
}
</script>
